import {useDebounce} from '@github-ui/use-debounce'
import {useSyncedState} from '@github-ui/use-synced-state'
import {SearchIcon, XCircleFillIcon} from '@primer/octicons-react'
import {FormControl, TextInput} from '@primer/react'

import styles from './RepositoryProjects.module.css'

interface ProjectSearchInputProps {
  /** Current query value from the URL (`?q=`). */
  query: string
  /** Called with the new query, debounced while the user types. */
  onQueryChange: (query: string) => void
}

const LABEL = 'Search projects'
const PLACEHOLDER = 'Search by name…'
const DEBOUNCE_MS = 500

export function ProjectSearchInput({query, onQueryChange}: ProjectSearchInputProps) {
  // Local input value that mirrors the URL `query` prop. Resyncs when the URL changes
  // externally (e.g. back/forward navigation) without an extra render cycle.
  const [value, setValue] = useSyncedState(query)

  const debouncedOnQueryChange = useDebounce(onQueryChange, DEBOUNCE_MS)

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = event.target.value
    setValue(next)
    debouncedOnQueryChange(next)
  }

  const handleClear = () => {
    setValue('')
    // Clear immediately, no debounce.
    debouncedOnQueryChange.cancel()
    onQueryChange('')
  }

  return (
    <FormControl className={styles.searchInputWrapper}>
      <FormControl.Label visuallyHidden>{LABEL}</FormControl.Label>
      <TextInput
        block
        type="search"
        value={value}
        onChange={handleChange}
        placeholder={PLACEHOLDER}
        leadingVisual={SearchIcon}
        trailingAction={
          value ? (
            <TextInput.Action onClick={handleClear} icon={XCircleFillIcon} aria-label="Clear search query" />
          ) : undefined
        }
      />
    </FormControl>
  )
}
